import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, CheckCircle, XCircle, UserPlus, Users, Truck, KeyRound, LogIn, LogOut, RotateCcw, UserCog, GitMerge, EyeOff, Eye, Search, Clock, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Format last activity time as relative time (e.g., "5 min ago", "2 hours ago", "Yesterday")
const formatLastSeen = (lastActivity) => {
  if (!lastActivity) return null;
  
  try {
    const date = new Date(lastActivity);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    // For older dates, show the actual date
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

const Customers = () => {
  const [manualCustomers, setManualCustomers] = useState([]);
  const [registeredCustomers, setRegisteredCustomers] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [customerToRename, setCustomerToRename] = useState(null);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [renaming, setRenaming] = useState(false);
  
  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [sourceCustomer, setSourceCustomer] = useState(null);
  const [targetCustomerId, setTargetCustomerId] = useState('');
  const [deactivateSource, setDeactivateSource] = useState(true);
  const [merging, setMerging] = useState(false);
  
  // Toggle active status state
  const [togglingActive, setTogglingActive] = useState(null);
  
  // Filter state
  const [showHiddenCustomers, setShowHiddenCustomers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Bulk bank alias manager state
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [aliasEdits, setAliasEdits] = useState({});
  const [aliasSearch, setAliasSearch] = useState('');
  const [savingAliases, setSavingAliases] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    bank_deposit_alias: '',
    affiliated_distributor_id: ''
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  useEffect(() => {
    fetchManualCustomers();
    fetchDistributors();
    if (isAdmin) {
      fetchRegisteredCustomers();
    }
  }, [isAdmin]);

  const fetchManualCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, getAuthHeader());
      setManualCustomers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Manager access required to view customers');
      } else {
        toast.error('Failed to fetch customers');
      }
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await axios.get(`${API}/distributors/list`, getAuthHeader());
      setDistributors(response.data);
    } catch (error) {
      console.error('Failed to fetch distributors');
    }
  };

  const fetchRegisteredCustomers = async () => {
    try {
      const response = await axios.get(`${API}/registered-customers`, getAuthHeader());
      setRegisteredCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch registered customers');
    }
  };

  const openAliasManager = () => {
    const edits = {};
    manualCustomers.forEach(c => { edits[c.id] = c.bank_deposit_alias || ''; });
    setAliasEdits(edits);
    setAliasSearch('');
    setAliasDialogOpen(true);
  };

  const saveAliases = async () => {
    const changed = manualCustomers.filter(c => (c.bank_deposit_alias || '') !== (aliasEdits[c.id] || ''));
    if (changed.length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSavingAliases(true);
    try {
      await Promise.all(changed.map(c =>
        axios.put(`${API}/customers/${c.id}/bank-alias`, { bank_deposit_alias: aliasEdits[c.id] || null }, getAuthHeader())
      ));
      toast.success(`Saved aliases for ${changed.length} customer${changed.length > 1 ? 's' : ''}`);
      window.dispatchEvent(new Event('unmatchedDepositsChanged'));
      await fetchManualCustomers();
      setAliasDialogOpen(false);
    } catch (e) {
      toast.error('Failed to save some aliases');
    } finally {
      setSavingAliases(false);
    }
  };

  // Merge both lists and add source indicator
  const getAllCustomers = () => {
    const manual = manualCustomers.map(c => ({ ...c, source: 'manual', is_approved: true }));
    const registered = registeredCustomers.map(c => ({ ...c, source: 'registered' }));
    
    // Remove duplicates based on ID (registered customers that were approved are in both lists)
    const registeredIds = new Set(registered.map(r => r.id));
    const uniqueManual = manual.filter(m => !registeredIds.has(m.id));
    
    let combined = [...registered, ...uniqueManual];
    
    // Filter out hidden customers unless showHiddenCustomers is true
    if (!showHiddenCustomers) {
      combined = combined.filter(c => c.is_active !== false);
    }
    
    // Filter by search query (name, email, or phone)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      combined = combined.filter(c => 
        (c.name && c.name.toLowerCase().includes(query)) ||
        (c.email && c.email.toLowerCase().includes(query)) ||
        (c.phone && c.phone.toLowerCase().includes(query))
      );
    }
    
    return combined;
  };
  
  // Count hidden customers
  const getHiddenCount = () => {
    const manual = manualCustomers.map(c => ({ ...c, source: 'manual' }));
    const registered = registeredCustomers.map(c => ({ ...c, source: 'registered' }));
    const registeredIds = new Set(registered.map(r => r.id));
    const uniqueManual = manual.filter(m => !registeredIds.has(m.id));
    const all = [...registered, ...uniqueManual];
    return all.filter(c => c.is_active === false).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData, getAuthHeader());
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers`, formData, getAuthHeader());
        toast.success('Customer added successfully');
      }
      handleClose();
      fetchManualCustomers();
      if (isAdmin) fetchRegisteredCustomers(); // Refresh registered customers too
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      bank_deposit_alias: customer.bank_deposit_alias || '',
      affiliated_distributor_id: customer.affiliated_distributor_id || ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCustomer(null);
    setFormData({ name: '', email: '', phone: '', address: '', bank_deposit_alias: '', affiliated_distributor_id: '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API}/customers/${id}`, getAuthHeader());
      toast.success('Customer deleted successfully');
      fetchManualCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const handleApproveCustomer = async (customerId, customerName) => {
    try {
      await axios.put(`${API}/users/${customerId}/approve`, {}, getAuthHeader());
      toast.success(`${customerName} has been approved`);
      fetchRegisteredCustomers();
      fetchManualCustomers();
    } catch (error) {
      toast.error('Failed to approve customer');
    }
  };

  const handleToggleLogin = async (customerId, customerName) => {
    try {
      const response = await axios.put(`${API}/customers/${customerId}/toggle-login`, {}, getAuthHeader());
      const { is_login_enabled, email_sent, message } = response.data;
      
      if (is_login_enabled) {
        if (email_sent) {
          toast.success(`Login enabled for ${customerName}. Welcome email sent with login credentials!`);
        } else {
          toast.success(`Login enabled for ${customerName}. Default password: 123456`, {
            description: 'No email on file - please inform customer manually'
          });
        }
      } else {
        toast.success(`Login disabled for ${customerName}`);
      }
      fetchManualCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle login');
    }
  };

  const handleResetPassword = async (customerId, customerName) => {
    if (!window.confirm(`Reset password for ${customerName} to default "123456"?`)) return;
    try {
      await axios.put(`${API}/customers/${customerId}/reset-password`, {}, getAuthHeader());
      toast.success(`Password reset for ${customerName}. New password: 123456`);
      fetchManualCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openRenameDialog = (customer) => {
    setCustomerToRename(customer);
    setNewCustomerName(customer.name);
    setRenameDialogOpen(true);
  };

  const handleRenameCustomer = async () => {
    if (!customerToRename || !newCustomerName.trim()) return;
    
    const trimmedName = newCustomerName.trim();
    if (trimmedName === customerToRename.name) {
      toast.error('New name is the same as current name');
      return;
    }
    
    setRenaming(true);
    try {
      const response = await axios.put(
        `${API}/customers/${customerToRename.id}/rename`,
        { new_name: trimmedName },
        getAuthHeader()
      );
      
      const updates = response.data.updates;
      toast.success(
        `Customer renamed from "${customerToRename.name}" to "${trimmedName}"`,
        {
          description: `Updated: ${updates.order_requests} orders, ${updates.pickups} pickups, ${updates.account_entries} account entries`
        }
      );
      
      setRenameDialogOpen(false);
      setCustomerToRename(null);
      setNewCustomerName('');
      fetchManualCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename customer');
    } finally {
      setRenaming(false);
    }
  };

  // Merge/Migrate customer functions
  const openMergeDialog = (customer) => {
    setSourceCustomer(customer);
    setTargetCustomerId('');
    setDeactivateSource(true);
    setMergeDialogOpen(true);
  };

  const handleMergeCustomers = async () => {
    if (!sourceCustomer || !targetCustomerId) return;
    
    const targetCustomer = manualCustomers.find(c => c.id === targetCustomerId);
    if (!targetCustomer) {
      toast.error('Target customer not found');
      return;
    }
    
    if (sourceCustomer.id === targetCustomerId) {
      toast.error('Cannot merge a customer with itself');
      return;
    }
    
    setMerging(true);
    try {
      const response = await axios.post(
        `${API}/customers/merge`,
        {
          source_customer_name: sourceCustomer.name,
          target_customer_name: targetCustomer.name,
          deactivate_source: deactivateSource
        },
        getAuthHeader()
      );
      
      const migrations = response.data.migrations;
      const total = response.data.total_records_migrated;
      
      toast.success(
        `Successfully migrated all data from "${sourceCustomer.name}" to "${targetCustomer.name}"`,
        {
          description: `Migrated ${total} records: ${migrations.order_requests} orders, ${migrations.pickups} pickups, ${migrations.account_entries} account entries${response.data.source_deactivated ? '. Source customer deactivated.' : ''}`
        }
      );
      
      setMergeDialogOpen(false);
      setSourceCustomer(null);
      setTargetCustomerId('');
      fetchManualCustomers();
      if (isAdmin) fetchRegisteredCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge customers');
    } finally {
      setMerging(false);
    }
  };

  // Toggle customer active status (show/hide from dropdowns)
  const handleToggleActive = async (customer) => {
    const newStatus = !customer.is_active;
    const confirmMessage = newStatus 
      ? `Are you sure you want to restore "${customer.name}"?\n\nThis customer will appear in all dropdown lists again.`
      : `Are you sure you want to hide "${customer.name}" from all dropdown lists?\n\nThis customer will be removed from customer selection dropdowns across the app, but their historical data will be preserved.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setTogglingActive(customer.id);
    try {
      const response = await axios.put(
        `${API}/customers/${customer.id}/toggle-active`,
        { is_active: newStatus },
        getAuthHeader()
      );
      
      toast.success(response.data.message);
      fetchManualCustomers();
      if (isAdmin) fetchRegisteredCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer status');
    } finally {
      setTogglingActive(null);
    }
  };

  const handleRevokeCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Are you sure you want to revoke approval for ${customerName}?`)) return;
    try {
      await axios.put(`${API}/users/${customerId}/revoke`, {}, getAuthHeader());
      toast.success(`Approval revoked for ${customerName}`);
      fetchRegisteredCustomers();
      fetchManualCustomers();
    } catch (error) {
      toast.error('Failed to revoke approval');
    }
  };

  const handleDeleteRegistered = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this registered customer? This will also delete their user account.')) return;
    try {
      await axios.delete(`${API}/users/${customerId}`, getAuthHeader());
      toast.success('Customer account deleted');
      fetchRegisteredCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const allCustomers = getAllCustomers();
  const hiddenCount = getHiddenCount();
  const pendingCount = registeredCustomers.filter(c => c.is_approved !== true).length;
  const totalCount = allCustomers.length;
  const approvedCount = allCustomers.filter(c => c.is_approved === true).length;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Customers
          </h1>
          <p className="text-lg text-slate-600 mt-2">Manage your customer database</p>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={openAliasManager} data-testid="manage-bank-aliases-button" className="border-sky-300 text-sky-700 hover:bg-sky-50">
            <Landmark className="w-4 h-4 mr-2" />
            Bank Aliases
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button" className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="customer-dialog">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="customer-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="customer-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="address">Address (Optional)</Label>
                <Textarea
                  id="address"
                  data-testid="customer-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="bank_deposit_alias">Bank Deposit Name / Alias (Optional)</Label>
                <Input
                  id="bank_deposit_alias"
                  data-testid="customer-bank-alias-input"
                  value={formData.bank_deposit_alias}
                  onChange={(e) => setFormData({ ...formData, bank_deposit_alias: e.target.value })}
                  placeholder="e.g. Mrs Okuro, Mom Ayedade"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Name(s) as they appear in bank deposit descriptions. Used to auto-record payments from Bank Reconciliation. Separate multiple names with commas.
                </p>
              </div>
              <div>
                <Label htmlFor="distributor">Affiliated Distributor (Optional)</Label>
                <Select
                  value={formData.affiliated_distributor_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, affiliated_distributor_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="customer-distributor-select">
                    <SelectValue placeholder="Select a distributor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Distributor</SelectItem>
                    {distributors.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Commission will be calculated for the affiliated distributor
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="customer-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingCustomer ? 'Update' : 'Add'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Bank Alias Manager */}
        <Dialog open={aliasDialogOpen} onOpenChange={setAliasDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="bank-alias-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Landmark className="w-5 h-5 text-sky-600" />
                Manage Bank Deposit Aliases
              </DialogTitle>
              <DialogDescription>
                Set the name(s) as they appear in bank deposit descriptions so payments auto-record into Customer Account Records. Separate multiple names with commas.
              </DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search customers..."
                value={aliasSearch}
                onChange={(e) => setAliasSearch(e.target.value)}
                className="pl-9"
                data-testid="alias-search-input"
              />
            </div>
            <div className="max-h-[50vh] overflow-y-auto border rounded-md divide-y">
              {manualCustomers
                .filter(c => c.is_active !== false)
                .filter(c => !aliasSearch.trim() || (c.name || '').toLowerCase().includes(aliasSearch.toLowerCase().trim()))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-2 hover:bg-slate-50" data-testid={`alias-row-${c.id}`}>
                    <div className="w-40 shrink-0 text-sm font-medium text-slate-700 truncate" title={c.name}>{c.name}</div>
                    <Input
                      value={aliasEdits[c.id] ?? ''}
                      onChange={(e) => setAliasEdits(prev => ({ ...prev, [c.id]: e.target.value }))}
                      placeholder="e.g. Mrs Okuro, Mom Ayedade"
                      className="h-8 text-sm flex-1"
                      data-testid={`alias-input-${c.id}`}
                    />
                  </div>
                ))}
              {manualCustomers.filter(c => c.is_active !== false).length === 0 && (
                <p className="text-sm text-slate-400 p-4 text-center">No customers found.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAliasDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveAliases} disabled={savingAliases} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-aliases-button">
                {savingAliases ? 'Saving...' : 'Save Aliases'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Total Customers</p>
              <p className="text-2xl font-bold text-slate-900">{totalCount}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Approved</p>
              <p className="text-2xl font-bold text-emerald-600">{approvedCount}</p>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="card p-4 bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Combined Customer Table */}
      <div className="card">
        {/* Search and Filter Bar */}
        <div className="px-4 py-3 border-b bg-slate-50 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white"
              data-testid="customer-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Filter Toggle for Hidden Customers */}
          {isAdmin && hiddenCount > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-hidden"
                  checked={showHiddenCustomers}
                  onCheckedChange={setShowHiddenCustomers}
                  className="data-[state=checked]:bg-slate-600"
                  data-testid="show-hidden-toggle"
                />
                <Label htmlFor="show-hidden" className="text-sm text-slate-600 cursor-pointer">
                  Show hidden
                </Label>
              </div>
              <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
                {hiddenCount} hidden
              </span>
              {showHiddenCustomers && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <EyeOff className="w-3 h-3" />
                  Showing hidden
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Search Results Info */}
        {searchQuery && (
          <div className="px-4 py-2 bg-blue-50 border-b text-sm text-blue-700">
            Found {allCustomers.length} customer{allCustomers.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Distributor</th>
                <th>Source</th>
                {isAdmin && <th>Login</th>}
                {isAdmin && <th>Online</th>}
                {isAdmin && <th>Status</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-testid="customers-table-body">
              {allCustomers.map((customer) => (
                <tr key={`${customer.source}-${customer.id}`} className={customer.is_active === false ? 'opacity-60' : ''}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      {customer.name}
                      {customer.is_active === false && (
                        <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-xs font-medium">
                          Hidden
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{customer.email || '-'}</td>
                  <td>{customer.phone || '-'}</td>
                  <td>
                    {customer.affiliated_distributor_name ? (
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        {customer.affiliated_distributor_name}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">Direct</span>
                    )}
                  </td>
                  <td>
                    {customer.source === 'registered' ? (
                      <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                        <UserPlus className="w-3 h-3" />
                        Registered
                      </span>
                    ) : (
                      <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Manual
                      </span>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      {customer.source === 'manual' && (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={customer.is_login_enabled || false}
                            onCheckedChange={() => handleToggleLogin(customer.id, customer.name)}
                            className="data-[state=checked]:bg-emerald-600"
                          />
                          {customer.is_login_enabled && (
                            <button
                              onClick={() => handleResetPassword(customer.id, customer.name)}
                              className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                              title="Reset Password to 123456"
                            >
                              <RotateCcw className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                  {isAdmin && (
                    <td>
                      {customer.is_online ? (
                        <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                          Online
                        </span>
                      ) : (
                        <div className="text-xs">
                          <span className="text-slate-500">Offline</span>
                          {customer.last_activity && (
                            <div className="text-slate-400 flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" />
                              {formatLastSeen(customer.last_activity)}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                  {isAdmin && (
                    <td>
                      {customer.is_approved === true ? (
                        <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Approved
                        </span>
                      ) : (
                        <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    <div className="flex gap-1">
                      {/* Approve/Revoke buttons for registered customers (Admin only) */}
                      {isAdmin && customer.source === 'registered' && (
                        <>
                          {customer.is_approved !== true ? (
                            <button
                              data-testid={`approve-customer-${customer.id}`}
                              onClick={() => handleApproveCustomer(customer.id, customer.name)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                              title="Approve Customer"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              data-testid={`revoke-customer-${customer.id}`}
                              onClick={() => handleRevokeCustomer(customer.id, customer.name)}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                              title="Revoke Approval"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                      
                      {/* Edit button - for manual customers OR Admin can edit any customer */}
                      {(customer.source === 'manual' || isAdmin) && (
                        <button
                          data-testid={`edit-customer-${customer.id}`}
                          onClick={() => handleEdit(customer)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit Customer"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Rename button for all customers (Admin only) */}
                      {isAdmin && (
                        <button
                          data-testid={`rename-customer-${customer.id}`}
                          onClick={() => openRenameDialog(customer)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="Rename Customer (keeps all data)"
                        >
                          <UserCog className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Merge button for all customers (Admin only) */}
                      {isAdmin && (
                        <button
                          data-testid={`merge-customer-${customer.id}`}
                          onClick={() => openMergeDialog(customer)}
                          className="p-2 text-teal-600 hover:bg-teal-50 rounded transition-colors"
                          title="Merge into another customer (migrate all data)"
                        >
                          <GitMerge className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Toggle Active/Inactive button (Admin only) */}
                      {isAdmin && (
                        <button
                          data-testid={`toggle-active-${customer.id}`}
                          onClick={() => handleToggleActive(customer)}
                          disabled={togglingActive === customer.id}
                          className={`p-2 rounded transition-colors ${
                            customer.is_active === false
                              ? 'text-emerald-600 hover:bg-emerald-50'
                              : 'text-slate-500 hover:bg-slate-50'
                          }`}
                          title={customer.is_active === false ? 'Restore to dropdown lists' : 'Remove from dropdown lists'}
                        >
                          {togglingActive === customer.id ? (
                            <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                          ) : customer.is_active === false ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      
                      {/* Delete button */}
                      <button
                        data-testid={`delete-customer-${customer.id}`}
                        onClick={() => customer.source === 'registered' ? handleDeleteRegistered(customer.id) : handleDelete(customer.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete Customer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {allCustomers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No customers yet. Add your first customer or share the registration link!
            </div>
          )}
        </div>
      </div>

      {/* Info note for Admin */}
      {isAdmin && (
        <div className="card p-4 bg-blue-50 border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Registered customers must be approved before they can submit order requests. Manual customers are automatically approved.
          </p>
        </div>
      )}

      {/* Rename Customer Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <UserCog className="w-5 h-5" />
              Rename Customer
            </DialogTitle>
            <DialogDescription>
              Change the customer's name while keeping all their historical data (orders, pickups, payments, etc.)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Current Name:</p>
              <p className="font-semibold text-lg">{customerToRename?.name}</p>
            </div>
            
            <div>
              <Label htmlFor="new_name">New Name</Label>
              <Input
                id="new_name"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder="Enter new customer name"
                className="mt-1"
              />
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>What will be updated:</strong>
              </p>
              <ul className="text-sm text-amber-700 list-disc list-inside mt-1">
                <li>Customer profile</li>
                <li>All order requests</li>
                <li>All order notifications</li>
                <li>All pickup records</li>
                <li>All account entries & payments</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRenameCustomer}
              disabled={renaming || !newCustomerName.trim() || newCustomerName.trim() === customerToRename?.name}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {renaming ? 'Renaming...' : 'Rename Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Customer Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <GitMerge className="w-5 h-5" />
              Merge Customer Accounts
            </DialogTitle>
            <DialogDescription>
              Transfer all data from one customer account to another. Use this when a customer registered twice with different names.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Source Customer */}
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-700 mb-1">Migrate FROM (Source):</p>
              <p className="text-lg font-bold text-red-900">{sourceCustomer?.name}</p>
              <p className="text-xs text-red-600 mt-1">All data from this account will be transferred</p>
            </div>
            
            {/* Arrow */}
            <div className="flex justify-center">
              <div className="text-teal-500 text-2xl">↓</div>
            </div>
            
            {/* Target Customer */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-700 mb-2">Migrate TO (Target):</p>
              <Select value={targetCustomerId} onValueChange={setTargetCustomerId}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select target customer..." />
                </SelectTrigger>
                <SelectContent>
                  {manualCustomers
                    .filter(c => c.id !== sourceCustomer?.id)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            
            {/* Deactivate option */}
            <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
              <Checkbox 
                id="deactivate" 
                checked={deactivateSource}
                onCheckedChange={setDeactivateSource}
              />
              <label htmlFor="deactivate" className="text-sm text-slate-700 cursor-pointer">
                Deactivate source account after merge (recommended)
              </label>
            </div>
            
            {/* Warning */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>⚠️ This will migrate:</strong>
              </p>
              <ul className="text-sm text-amber-700 list-disc list-inside mt-1">
                <li>All order requests</li>
                <li>All order notifications</li>
                <li>All pickup records</li>
                <li>All account entries & payments</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMergeCustomers}
              disabled={merging || !targetCustomerId}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {merging ? 'Merging...' : 'Merge Accounts'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
